// Servicio para interactuar con la API de Spotify

class SpotifyService {
  constructor() {
    this.clientId = process.env.REACT_APP_SPOTIFY_CLIENT_ID;
    this.clientSecret = process.env.REACT_APP_SPOTIFY_CLIENT_SECRET;
    this.accessToken = null;
    this.tokenExpiration = null;
  }

  // Obtener token de acceso usando Client Credentials Flow
  async getAccessToken() {
    // Si ya tenemos un token válido, usarlo
    if (this.accessToken && this.tokenExpiration && Date.now() < this.tokenExpiration) {
      console.log('✓ Usando token de Spotify existente');
      return this.accessToken;
    }

    try {
      console.log('🔑 Solicitando token de Spotify...');

      // Verificar que las credenciales existen
      if (!this.clientId || !this.clientSecret) {
        console.error('❌ Credenciales de Spotify no configuradas');
        console.log('Client ID:', this.clientId ? 'Configurado' : 'NO CONFIGURADO');
        console.log('Client Secret:', this.clientSecret ? 'Configurado' : 'NO CONFIGURADO');
        throw new Error('Credenciales de Spotify no configuradas en .env');
      }

      const response = await fetch('https://accounts.spotify.com/api/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': 'Basic ' + btoa(this.clientId + ':' + this.clientSecret)
        },
        body: 'grant_type=client_credentials'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Respuesta de Spotify:', response.status, errorText);
        throw new Error(`Error obteniendo token de Spotify: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      this.accessToken = data.access_token;
      // El token expira en 3600 segundos (1 hora), guardamos cuando expira
      this.tokenExpiration = Date.now() + (data.expires_in * 1000);

      console.log('✅ Token de Spotify obtenido exitosamente');
      return this.accessToken;
    } catch (error) {
      console.error('❌ Error obteniendo token de Spotify:', error);
      throw error;
    }
  }

  // Buscar una canción en Spotify
  async searchTrack(trackName, artistName) {
    try {
      const token = await this.getAccessToken();
      const query = encodeURIComponent(`${trackName} ${artistName}`);

      const response = await fetch(
        `https://api.spotify.com/v1/search?q=${query}&type=track&limit=1`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        // Si es rate limit (429), lanzar error específico
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          console.warn(`⏳ Rate limit alcanzado. Retry after: ${retryAfter}s`);
          throw new Error('429');
        }
        throw new Error('Error buscando en Spotify');
      }

      const data = await response.json();

      if (data.tracks && data.tracks.items && data.tracks.items.length > 0) {
        const track = data.tracks.items[0];
        return {
          id: track.id,
          name: track.name,
          artist: track.artists[0].name,
          album: track.album.name,
          image: track.album.images[0]?.url || null, // Imagen más grande
          imageSmall: track.album.images[2]?.url || null,
          imageMedium: track.album.images[1]?.url || null,
          previewUrl: track.preview_url,
          spotifyUrl: track.external_urls.spotify,
          duration: track.duration_ms
        };
      }

      return null;
    } catch (error) {
      if (error.message === '429') {
        throw error; // Re-lanzar el error 429 para manejarlo arriba
      }
      console.error(`⚠️ Error buscando "${trackName}" en Spotify:`, error);
      return null;
    }
  }

  // Buscar múltiples canciones en paralelo
  async searchTracks(tracks) {
    const promises = tracks.map(track =>
      this.searchTrack(track.title, track.artist)
    );
    return await Promise.all(promises);
  }

  // Obtener información de una canción por ID
  async getTrack(trackId) {
    try {
      const token = await this.getAccessToken();

      const response = await fetch(
        `https://api.spotify.com/v1/tracks/${trackId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error obteniendo canción de Spotify');
      }

      const track = await response.json();
      return {
        id: track.id,
        name: track.name,
        artist: track.artists[0].name,
        album: track.album.name,
        image: track.album.images[0]?.url || null,
        previewUrl: track.preview_url,
        spotifyUrl: track.external_urls.spotify,
        duration: track.duration_ms
      };
    } catch (error) {
      console.error('Error obteniendo track de Spotify:', error);
      return null;
    }
  }

  // Obtener tracks de una playlist específica
  async getPlaylistTracks(playlistId, limit = 50) {
    try {
      const token = await this.getAccessToken();

      const response = await fetch(
        `https://api.spotify.com/v1/playlists/${playlistId}/tracks?limit=${limit}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Error ${response.status} obteniendo playlist ${playlistId}:`, errorText);

        // Si es 404, la playlist no existe
        if (response.status === 404) {
          console.warn(`⚠️ Playlist ${playlistId} no encontrada`);
        }

        return [];
      }

      const data = await response.json();

      if (!data.items || !Array.isArray(data.items)) {
        console.warn(`⚠️ Formato de respuesta inesperado para playlist ${playlistId}`);
        return [];
      }

      return data.items
        .filter(item => item && item.track) // Filtrar items null o sin track
        .map(item => ({
          id: item.track.id,
          title: item.track.name,
          artist: item.track.artists[0]?.name || 'Unknown Artist',
          album: item.track.album?.name || 'Unknown Album',
          image: item.track.album?.images[0]?.url || null,
          previewUrl: item.track.preview_url,
          spotifyUrl: item.track.external_urls?.spotify || null,
          duration: item.track.duration_ms
        }));
    } catch (error) {
      console.error(`❌ Error obteniendo playlist ${playlistId}:`, error);
      return [];
    }
  }

  // Obtener playlists destacadas de Spotify
  async getFeaturedPlaylists(limit = 10) {
    try {
      const token = await this.getAccessToken();

      const response = await fetch(
        `https://api.spotify.com/v1/browse/featured-playlists?limit=${limit}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error obteniendo playlists destacadas');
      }

      const data = await response.json();
      return data.playlists.items.map(playlist => ({
        id: playlist.id,
        name: playlist.name,
        description: playlist.description,
        image: playlist.images[0]?.url || null,
        tracksTotal: playlist.tracks.total
      }));
    } catch (error) {
      console.error('Error obteniendo playlists destacadas:', error);
      return [];
    }
  }

  // Obtener nuevos lanzamientos
  async getNewReleases(limit = 20) {
    try {
      const token = await this.getAccessToken();

      const response = await fetch(
        `https://api.spotify.com/v1/browse/new-releases?limit=${limit}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error obteniendo nuevos lanzamientos');
      }

      const data = await response.json();
      return data.albums.items.map(album => ({
        id: album.id,
        title: album.name,
        artist: album.artists[0].name,
        image: album.images[0]?.url || null,
        spotifyUrl: album.external_urls.spotify,
        releaseDate: album.release_date
      }));
    } catch (error) {
      console.error('Error obteniendo nuevos lanzamientos:', error);
      return [];
    }
  }

  // Buscar canciones populares por género
  async searchByGenre(genre, limit = 15) {
    try {
      const token = await this.getAccessToken();

      // Buscar tracks populares del género
      const query = encodeURIComponent(`genre:"${genre}"`);
      const response = await fetch(
        `https://api.spotify.com/v1/search?q=${query}&type=track&limit=${limit}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        console.error(`Error buscando género ${genre}`);
        return [];
      }

      const data = await response.json();

      if (!data.tracks || !data.tracks.items) {
        return [];
      }

      return data.tracks.items.map(track => ({
        id: track.id,
        title: track.name,
        artist: track.artists[0]?.name || 'Unknown Artist',
        album: track.album?.name || 'Unknown Album',
        image: track.album?.images[0]?.url || null,
        previewUrl: track.preview_url,
        spotifyUrl: track.external_urls?.spotify || null,
        duration: track.duration_ms
      }));
    } catch (error) {
      console.error(`Error buscando género ${genre}:`, error);
      return [];
    }
  }

  // Obtener recomendaciones basadas en géneros
  async getRecommendationsByGenre(genres, limit = 15) {
    try {
      const token = await this.getAccessToken();

      const genresParam = Array.isArray(genres) ? genres.join(',') : genres;
      const response = await fetch(
        `https://api.spotify.com/v1/recommendations?seed_genres=${encodeURIComponent(genresParam)}&limit=${limit}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        console.error(`Error obteniendo recomendaciones para ${genresParam}`);
        return [];
      }

      const data = await response.json();

      if (!data.tracks) {
        return [];
      }

      return data.tracks.map(track => ({
        id: track.id,
        title: track.name,
        artist: track.artists[0]?.name || 'Unknown Artist',
        album: track.album?.name || 'Unknown Album',
        image: track.album?.images[0]?.url || null,
        previewUrl: track.preview_url,
        spotifyUrl: track.external_urls?.spotify || null,
        duration: track.duration_ms
      }));
    } catch (error) {
      console.error(`Error obteniendo recomendaciones:`, error);
      return [];
    }
  }
}

// Exportar una única instancia
export const spotifyService = new SpotifyService();
